import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../utils/intl';
import { useDateLabs } from '../../../../../hooks/api/useDateLabs';
import * as Card from '../../../../v2BaseComponents/Card';
import {
    dedupeLabs,
    getLabValueName,
    getLabValueValueDisplay,
    getReferenceRangeDisplay,
    hasFullReferenceRange,
    isHighValue,
    isOutOfRange,
} from '../../../../../utils/medicalDataUtils';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import getDateString from '../../../../../utils/getDateString';
import LabValueRangeBar from '../../../../shared/LabValueRangeBar';
import { MedicalDataTypes } from '../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';

const intl = getIntl('date-labs-screen', {
    high: 'High',
    low: 'Low',
    labsOn: 'Labs on {date}',
    otherLabs: 'Other Labs',
});

export type DateLabsScreenOwnProps = {};

export type DateLabsScreenProps = DateLabsScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'date-labs'>;
};

type OnAnalytePress = (resourceId: string, code: string) => void;

const DateLabsScreen: React.FC<DateLabsScreenProps> = (props) => {
    const navigation = useProfilesTabNavigation();
    const { isLoadingDateLabs, dateLabs } = useDateLabs({
        medicalProfileId: props.route.params.profileId,
        date: props.route.params.date,
    });

    useSetHeaderTitle(
        intl('labsOn', {
            date: getDateString(props.route.params.date, 'date'),
        }),
    );

    const renderAnalyteSet = (
        name: string,
        analyteSet: MedicalDataTypes.PrimaryRecordLab[],
        key: string,
        onPress: OnAnalytePress,
    ) => (
        <React.Fragment key={key}>
            {analyteSet.map((analyte, index) => (
                <React.Fragment key={analyte.id + String(index)}>
                    {analyte.values.map((value, index) => {
                        const analyteCode = value.componentCoding?.code || analyte.labType.code;
                        return (
                            <Card.Card
                                key={analyte.id + String(index)}
                                onPress={() => onPress(analyte.id, analyteCode)}
                            >
                                <Card.CardContent>
                                    <Card.CardInlineContent>
                                        <Card.CardHeader>{getLabValueName(analyte, value)}</Card.CardHeader>
                                        {isOutOfRange(value) ? (
                                            <Card.CardChip title={intl(isHighValue(value) ? 'high' : 'low')} />
                                        ) : null}
                                    </Card.CardInlineContent>
                                    {!hasFullReferenceRange(value) ? (
                                        <Card.CardBody>{getLabValueValueDisplay(value)}</Card.CardBody>
                                    ) : null}
                                    <LabValueRangeBar labValue={value} />
                                    <Stack horizontal justify="center" fullWidth>
                                        {getReferenceRangeDisplay(value) ? (
                                            <Card.CardBody2>{getReferenceRangeDisplay(value) || ''}</Card.CardBody2>
                                        ) : null}
                                    </Stack>
                                </Card.CardContent>
                            </Card.Card>
                        );
                    })}
                </React.Fragment>
            ))}
        </React.Fragment>
    );

    const onAnalytePress: OnAnalytePress = (resourceId, labCode) => {
        navigation.navigate('lab-history', {
            profileId: props.route.params.profileId,
            labCode,
        });
    };
    return (
        <AnimateLoadedContent isLoading={isLoadingDateLabs}>
            {() => (
                <ProfileHeaderContainer profileId={props.route.params.profileId} bottomBorder>
                    <ActionButtonLayout scrollable>
                        <MainContentContainer style={{ width: '100%', height: '100%' }}>
                            <ContentStack>
                                {dateLabs.panels.map((lab, index) =>
                                    renderAnalyteSet(
                                        lab.panelName,
                                        dedupeLabs(lab.labs),
                                        String(index),
                                        onAnalytePress,
                                    ),
                                )}
                                {dateLabs.unmatchedLabs?.length
                                    ? renderAnalyteSet(
                                          intl('otherLabs'),
                                          dedupeLabs(dateLabs.unmatchedLabs),
                                          'unmatchedLabs',
                                          onAnalytePress,
                                      )
                                    : null}
                            </ContentStack>
                        </MainContentContainer>
                    </ActionButtonLayout>
                </ProfileHeaderContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(DateLabsScreen);
